import api, { getApiBaseUrl, getAccessToken } from "./api";
import type {
  Message,
  ChatRoomCreateRequest,
  MessageCreateRequest,
  LinkPreview,
} from "@/types/chat";
import type { User } from "@/types/user";

// Extended member type with user details
export interface ChatRoomMemberWithUser {
  id: number;
  room_id: number;
  user_id: string;
  last_read_at?: string;
  last_read_message_id?: number;
  joined_at: string;
  is_muted: boolean;
  is_favorite: boolean;
  user?: User;
}

// Chat room response with full details
export interface ChatRoomWithDetails {
  id: number;
  name?: string;
  room_type: "direct" | "group" | "project";
  /** 기능 연동 방 용도 코드 (CHAT_ROOM_PURPOSE_META 키, 예: customer_inquiry) — 없으면 일반 대화방 */
  purpose?: string | null;
  /** purpose 방의 처리 대기 건수 (예: 답변 대기 문의 수) — 읽어도 답변 전까지 남는다 */
  pending_count?: number | null;
  project_id?: number;
  created_by: string;
  created_at: string;
  members?: ChatRoomMemberWithUser[];
  last_message?: Message & { user?: User };
  unread_count?: number;
  member_count?: number;
}

export interface MessageWithUser extends Message {
  user?: User;
  user_name?: string;
}

// 대화 내용으로 채팅방 검색 결과 (방별 최신 일치 메시지 1건 + 일치 건수)
export interface RoomContentSearchResult {
  room_id: number;
  match_count: number;
  message_id: number;
  content: string;
  created_at: string;
  user_id: string;
  user_name?: string;
}

/**
 * multipart 업로드 공통 처리.
 * axios 대신 XHR 을 쓰는 이유는 업로드 진행률(onProgress) 때문.
 */
function postUpload(
  path: string,
  formData: FormData,
  onProgress?: (percent: number) => void
): Promise<Message> {
  const token = getAccessToken();
  const baseUrl = getApiBaseUrl();

  return new Promise<Message>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${baseUrl}${path}`);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    // Content-Type 은 설정하지 않음 - 브라우저가 multipart boundary 자동 설정

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          reject(new Error("Upload failed"));
        }
      } else {
        let detail = "Upload failed";
        try {
          detail = JSON.parse(xhr.responseText).detail || detail;
        } catch {
          // 413 등 비-JSON 응답은 기본 메시지 사용
        }
        reject(new Error(detail));
      }
    };
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.send(formData);
  });
}

export const chatService = {
  // Chat Rooms
  async getRooms(): Promise<ChatRoomWithDetails[]> {
    const response = await api.get<ChatRoomWithDetails[]>("/chat/rooms");
    return response.data;
  },

  async getRoom(roomId: number): Promise<ChatRoomWithDetails> {
    const response = await api.get<ChatRoomWithDetails>(`/chat/rooms/${roomId}`);
    return response.data;
  },

  async createRoom(data: ChatRoomCreateRequest): Promise<ChatRoomWithDetails> {
    const response = await api.post<ChatRoomWithDetails>("/chat/rooms", data);
    return response.data;
  },

  async createDirectRoom(userId: string): Promise<ChatRoomWithDetails> {
    const response = await api.post<ChatRoomWithDetails>("/chat/rooms", {
      room_type: "direct",
      member_ids: [userId],
    });
    return response.data;
  },

  async updateRoom(
    roomId: number,
    data: { name?: string }
  ): Promise<ChatRoomWithDetails> {
    const response = await api.patch<ChatRoomWithDetails>(
      `/chat/rooms/${roomId}`,
      data
    );
    return response.data;
  },

  // Messages
  async getMessages(
    roomId: number,
    params?: { before_id?: number; page_size?: number }
  ): Promise<MessageWithUser[]> {
    const response = await api.get<MessageWithUser[]>(
      `/chat/rooms/${roomId}/messages`,
      { params }
    );
    return response.data;
  },

  async sendMessage(
    roomId: number,
    data: MessageCreateRequest
  ): Promise<Message> {
    const response = await api.post<Message>(
      `/chat/rooms/${roomId}/messages`,
      data
    );
    return response.data;
  },

  // 링크 미리보기(OG 카드) 메타데이터 — 실패 시 { ok: false }
  async getLinkPreview(url: string): Promise<LinkPreview> {
    const response = await api.get<LinkPreview>("/chat/link-preview", {
      params: { url },
    });
    return response.data;
  },

  async updateMessage(messageId: number, content: string): Promise<Message> {
    const response = await api.put<Message>(`/chat/messages/${messageId}`, null, {
      params: { content },
    });
    return response.data;
  },

  async deleteMessage(messageId: number): Promise<void> {
    await api.delete(`/chat/messages/${messageId}`);
  },

  // Reactions
  async addReaction(messageId: number, emoji: string): Promise<void> {
    await api.post(`/chat/messages/${messageId}/reactions`, { emoji });
  },

  async removeReaction(messageId: number, emoji: string): Promise<void> {
    await api.delete(`/chat/messages/${messageId}/reactions/${emoji}`);
  },

  // File upload - XMLHttpRequest 사용 (업로드 진행률 onProgress 지원)
  // fetch 는 업로드 진행 이벤트를 제공하지 않아 XHR 로 구현
  async uploadFile(
    roomId: number,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<Message> {
    const formData = new FormData();
    formData.append("file", file);
    return postUpload(`/chat/rooms/${roomId}/upload`, formData, onProgress);
  },

  // 여러 파일을 메시지 하나에 묶어서 업로드 ('묶어서 보내기')
  // 파일당 메시지를 만드는 uploadFile 과 달리 첨부 N개짜리 메시지 1개가 생성된다.
  async uploadFilesBundle(
    roomId: number,
    files: File[],
    onProgress?: (percent: number) => void
  ): Promise<Message> {
    const formData = new FormData();
    for (const file of files) {
      formData.append("files", file);
    }
    return postUpload(`/chat/rooms/${roomId}/upload-bundle`, formData, onProgress);
  },

  // 묶음 메시지의 첨부 전체를 zip 으로 받기 (서버에서 압축)
  async downloadAllAttachments(messageId: number): Promise<Blob> {
    const response = await api.get(`/chat/messages/${messageId}/download-all`, {
      responseType: "blob",
    });
    return response.data;
  },

  // Get attachment presigned URL
  async getAttachmentUrl(attachmentId: number): Promise<{
    url: string;
    file_name: string;
    file_type: string;
    file_size: number;
  }> {
    const response = await api.get(`/chat/attachments/${attachmentId}/url`);
    return response.data;
  },

  // Room attachments (미디어/파일 목록)
  async getRoomAttachments(
    roomId: number,
    mediaType?: "image" | "file",
    page: number = 1,
    pageSize: number = 50
  ): Promise<import("@/types/chat").RoomAttachment[]> {
    const response = await api.get(`/chat/rooms/${roomId}/attachments`, {
      params: { media_type: mediaType, page, page_size: pageSize },
    });
    return response.data;
  },

  // Search
  async searchMessages(
    query: string,
    roomId?: number
  ): Promise<MessageWithUser[]> {
    const response = await api.get<MessageWithUser[]>("/chat/messages/search", {
      params: { query, room_id: roomId },
    });
    return response.data;
  },

  // 대화 내용으로 채팅방 검색 (내가 참여한 방 대상, 카카오톡 스타일)
  async searchRoomsByContent(
    query: string
  ): Promise<RoomContentSearchResult[]> {
    const response = await api.get<RoomContentSearchResult[]>(
      "/chat/messages/search-rooms",
      { params: { query } }
    );
    return response.data;
  },

  // Room members
  async addMember(roomId: number, userId: string): Promise<void> {
    await api.post(`/chat/rooms/${roomId}/members`, { user_id: userId });
  },

  async removeMember(roomId: number, userId: string): Promise<void> {
    await api.delete(`/chat/rooms/${roomId}/members/${userId}`);
  },

  async leaveRoom(roomId: number): Promise<void> {
    await api.post(`/chat/rooms/${roomId}/leave`);
  },

  async toggleMute(roomId: number): Promise<{
    success: boolean;
    is_muted: boolean;
    message: string;
  }> {
    const response = await api.post(`/chat/rooms/${roomId}/mute`);
    return response.data;
  },

  async toggleFavorite(roomId: number): Promise<{
    success: boolean;
    is_favorite: boolean;
    message: string;
  }> {
    const response = await api.post(`/chat/rooms/${roomId}/favorite`);
    return response.data;
  },

  // Forward attachment to other rooms (file sharing, no re-upload)
  async forwardAttachment(
    attachmentId: number,
    roomIds: number[]
  ): Promise<{
    success: boolean;
    forwarded_count: number;
    results: { room_id: number; message_id: number; attachment_id: number }[];
  }> {
    const response = await api.post(
      `/chat/attachments/${attachmentId}/forward`,
      { room_ids: roomIds }
    );
    return response.data;
  },
};

export default chatService;
